import '../styles/pages/Home.css';
import useReveal from '../components/useReveal';
import CountUp from '../components/CountUp';

/* Ticker items repeated twice for seamless loop */
const TICKER_ITEMS = [
  'Erythromycin Range', 'Silver Sulphadiazine', 'Diethyl Carbamazine Citrate',
  'Mefenamic Acid', 'Azithromycin', 'Ciprofloxacin HCl', 'Clarithromycin',
  'Diclofenac Range', 'Levofloxacin', 'Gliclazide', 'Fluconazole',
  'Ofloxacin', 'Ranolazine', 'Febuxostat',
];

const WHY_CARDS = [
  { n: '01', icon: '🎯', title: 'Competitive Pricing',          desc: 'We bring pharmaceutical-grade APIs at prices that allow formulators worldwide to build cost-effective product ranges without compromising on quality.' },
  { n: '02', icon: '🔬', title: 'Multi-Pharmacopoeia Compliance', desc: 'Every product meets the requirements of IP, BP, USP, EP, and IH standards — giving our clients the flexibility to serve any global market.' },
  { n: '03', icon: '🏭', title: 'WHO GMP Certified Plant',       desc: 'Our state-of-the-art Bahadurgarh facility operates under WHO Good Manufacturing Practice guidelines with rigorous quality control at every stage.' },
  { n: '04', icon: '📋', title: 'Drug Master Files Available',   desc: 'DMF documentation available for most products, supporting our clients in regulatory submissions and market approvals across jurisdictions.' },
  { n: '05', icon: '🤝', title: 'Long-Term Partnerships',        desc: "We don't just supply — we build lasting business relationships. Pharmchem is a reliable source of supply that formulators depend on year after year." },
  { n: '06', icon: '🌐', title: 'Global Export Network',         desc: 'Active export operations to 33+ countries across Asia, Africa, Europe, and the Americas, with MNC approvals in multiple markets.' },
];

const FEATURED_PRODUCTS = [
  { name: 'Erythromycin Stearate',        cas: '643-22-1',    tags: ['IP', 'BP', 'USP'] },
  { name: 'Silver Sulphadiazine',         cas: '22199-08-2',  tags: ['IP', 'USP'] },
  { name: 'Diethyl Carbamazine Citrate',  cas: '114-07-8',    tags: ['IP', 'BP', 'USP'] },
  { name: 'Mefenamic Acid',               cas: '61-68-7',     tags: ['IP', 'BP', 'USP', 'EP'] },
  { name: 'Azithromycin',                 cas: '83905-01-5',  tags: ['IP', 'BP', 'USP'] },
  { name: 'Ciprofloxacin Hydrochloride',  cas: '85721-33-1',  tags: ['IP', 'BP', 'USP'] },
  { name: 'Levofloxacin',                 cas: '100986-85-4', tags: ['IP', 'USP'] },
  { name: 'Gliclazide',                   cas: '21187-98-4',  tags: ['IP', 'BP', 'EP'] },
];

const CATEGORIES = [
  { n: '4', icon: '🦠', name: 'Anti-Infectives &\nAntibiotics',   sub: 'Erythromycin, Azithromycin, Clarithromycin +more' },
  { n: '5', icon: '💊', name: 'Anti-Inflammatory &\nAnalgesics',  sub: 'Diclofenac range, Mefenamic Acid, Aceclofenac' },
  { n: '4', icon: '🔬', name: 'Fluoroquinolone\nAntibiotics',     sub: 'Ciprofloxacin, Levofloxacin, Ofloxacin +more' },
  { n: '4', icon: '❤️', name: 'Cardiovascular &\nMetabolic',      sub: 'Ranolazine, Gliclazide, Febuxostat, Fluconazole' },
];

const TIMELINE = [
  { year: '1976', title: 'Foundation',               desc: 'Pharmchem was established in India with a vision to provide quality pharmaceutical ingredients at competitive prices to formulators globally.' },
  { year: '1985', title: 'First International Exports', desc: 'Crossed borders to begin supplying APIs to international markets, building the foundation for a global export network.' },
  { year: '1998', title: 'WHO GMP Certification',    desc: 'Achieved WHO GMP certification — a major milestone validating our commitment to international quality standards.' },
  { year: '2005', title: 'Erythromycin Leadership',  desc: 'Established Pharmchem as one of the top five global manufacturers of Erythromycin salts, with capacity expansions at Bahadurgarh.' },
  { year: '2015', title: 'Global Reach: 25+ Countries', desc: 'Scaled exports to over 25 countries across Asia, Africa, Europe and the Americas, gaining MNC approvals in multiple markets.' },
  { year: '2024', title: '33+ Countries & Expanding', desc: 'Today Pharmchem serves 33+ countries with a portfolio of 21 APIs and an unwavering focus on quality, reliability and long-term partnerships.' },
];

const TESTIMONIALS = [
  { init: 'R', quote: 'Pharmchem has been our preferred API supplier for over a decade. The quality is consistently excellent and their pricing makes our formulations highly competitive in the market.',  name: 'Rajesh Kumar',    role: 'Procurement Head, Leading Indian Pharma Co.' },
  { init: 'A', quote: "The reliability of Pharmchem's supply chain is exceptional. WHO GMP certification and consistent DMF documentation have simplified our regulatory processes significantly.",              name: 'Ahmed Al-Rashid', role: 'Director, Gulf Pharmaceutical Group, UAE' },
  { init: 'K', quote: 'We have been sourcing Erythromycin and Diclofenac from Pharmchem for our African operations. Outstanding quality, timely delivery, and a team that truly values long-term relationships.', name: 'Kofi Mensah',     role: 'CEO, West Africa Pharma Distributors, Ghana' },
];

function Home({ navigate }) {
  useReveal();

  return (
    <>
      {/* ─── HERO ─── */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-bg-shape"></div>
          <div className="hero-bg-shape"></div>
          <div className="hero-bg-grid"></div>
        </div>

        {/* Left content */}
        <div className="hero-content">
          <div className="hero-badge">
            <div className="hero-badge-dot"></div>
            WHO GMP Certified · Since 1976
          </div>

          <h1>
            Pharmaceutical
            <em>Excellence,</em>
            Delivered Globally
          </h1>

          <p className="hero-lead">
            India's trusted manufacturer of high-quality Active Pharmaceutical Ingredients —
            serving formulators across 33+ countries with cost-effective, pharmacopoeia-compliant APIs.
          </p>

          <div className="hero-actions">
            <button className="btn btn-red" onClick={() => navigate('products')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              View Product Catalogue
            </button>
            <button className="btn btn-outline" onClick={() => navigate('contact')}>
              Request a Quote
            </button>
          </div>

          <div className="hero-trust">
            {[
              { icon: '🏆', title: 'Top 5 Globally',  sub: 'Erythromycin Manufacturer' },
              { icon: '🌍', title: '33+ Countries',    sub: 'Export Footprint' },
              { icon: '⚗️', title: 'WHO GMP',          sub: 'Certified Facility' },
            ].map((t) => (
              <div className="trust-item" key={t.title}>
                <div className="trust-icon">{t.icon}</div>
                <div className="trust-text">
                  <strong>{t.title}</strong>
                  {t.sub}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right visual */}
        <div className="hero-visual">
          <div className="hero-stat-grid">
            <div className="hero-stat-card">
              <div className="stat-number" style={{ color: 'var(--crimson)' }}>WHO</div>
              <div className="stat-label-text">GMP Certified</div>
            </div>
            <div className="hero-stat-card accent">
              <div className="stat-number"><CountUp target={49} /></div>
              <div className="stat-label-text">Years of Excellence</div>
            </div>
            <div className="hero-stat-card">
              <div className="stat-number"><CountUp target={33} /></div>
              <div className="stat-label-text">Countries Served</div>
            </div>
            <div className="hero-stat-card">
              <div className="stat-number"><CountUp target={21} /></div>
              <div className="stat-label-text">API Products</div>
            </div>
          </div>

          <div className="hero-cert-strip">
            <div className="cert-icon-big">📜</div>
            <div className="cert-strip-text">
              <strong>Pharmacopoeia Compliance</strong><br />
              IP · BP · USP · EP · IH — all major global standards
            </div>
          </div>
        </div>
      </section>

      {/* ─── TICKER ─── */}
      <div className="ticker">
        <div className="ticker-inner">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
            <span key={i}>
              {item} <span className="dot">●</span>
            </span>
          ))}
        </div>
      </div>

      {/* ─── METRICS ─── */}
      <div className="metrics">
        <div className="metrics-grid">
          {[
            { prefix: '', target: 49, suffix: '+', label: 'Years Established' },
            { prefix: '', target: 33, suffix: '+', label: 'Countries Served' },
            { prefix: '', target: 21, suffix: '',  label: 'API Products' },
            { prefix: 'Top', target: null, suffix: ' 5', label: 'Erythromycin Manufacturer' },
            { prefix: '#',   target: null, suffix: '1',  label: 'DEC Citrate Producer' },
          ].map((m, i) => (
            <div className={`metric-item reveal stagger-${i + 1}`} key={i}>
              <div className="metric-num">
                {m.target !== null
                  ? <CountUp target={m.target} suffix={m.suffix} />
                  : <>{m.prefix}<span className="metric-unit">{m.suffix}</span></>
                }
              </div>
              <div className="metric-label">{m.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── WHY PHARMCHEM ─── */}
      <div className="why">
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="reveal">
            <div className="eyebrow">Why Choose Pharmchem</div>
            <h2 className="headline">
              The Standard for <em>Quality APIs</em><br />in Global Pharmaceuticals
            </h2>
          </div>
          <div className="why-grid">
            {WHY_CARDS.map((w, i) => (
              <div className={`why-card reveal stagger-${(i % 3) + 1}`} key={i}>
                <div className="why-number">{w.n}</div>
                <span className="why-icon">{w.icon}</span>
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── PRODUCT PREVIEW ─── */}
      <div className="products-preview">
        <div className="products-preview-inner">
          <div className="products-top reveal">
            <div>
              <div className="eyebrow">Our Portfolio</div>
              <h2 className="headline">Featured <em>APIs</em></h2>
            </div>
            <button className="btn btn-outline" onClick={() => navigate('products')}>
              View All 21 Products →
            </button>
          </div>

          <div className="product-cards-grid">
            {FEATURED_PRODUCTS.map((p, i) => (
              <div
                className={`product-mini-card reveal stagger-${(i % 4) + 1}`}
                key={i}
                onClick={() => navigate('products')}
              >
                <div className="dmf-dot"></div>
                <h4>{p.name}</h4>
                <div className="cas">CAS: {p.cas}</div>
                <div className="pharma-tags">
                  {p.tags.map((t) => <span className="ptag" key={t}>{t}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── CATEGORIES ─── */}
      <div className="categories">
        <div className="categories-inner">
          <div className="reveal" style={{ textAlign: 'center', maxWidth: 540, margin: '0 auto 0' }}>
            <div className="eyebrow">Therapeutic Categories</div>
            <h2 className="headline">APIs Across <em>Key Therapy Areas</em></h2>
          </div>

          <div className="cat-grid">
            {CATEGORIES.map((c, i) => (
              <div
                className={`cat-card reveal stagger-${i + 1}`}
                key={i}
                onClick={() => navigate('products')}
              >
                <div className="cat-bg-num">{c.n}</div>
                <div className="cat-icon">{c.icon}</div>
                <div className="cat-name" style={{ whiteSpace: 'pre-line' }}>{c.name}</div>
                <div className="cat-count">{c.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── TIMELINE ─── */}
      <div className="timeline">
        <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }} className="reveal">
          <div className="eyebrow">Our Journey</div>
          <h2 className="headline">49 Years of <em>Building Trust</em></h2>
        </div>

        <div className="timeline-inner">
          <div className="timeline-line"></div>
          {TIMELINE.map((t, i) => (
            <div className="timeline-item reveal" key={i}>
              <div className="tl-dot"></div>
              {/* Left / right columns alternate */}
              <div className="tl-side" style={{ textAlign: i % 2 === 0 ? 'right' : 'left', paddingRight: i % 2 === 0 ? 40 : 0, paddingLeft: i % 2 === 1 ? 40 : 0 }}>
                {i % 2 === 0 && (
                  <div className="tl-content">
                    <div className="tl-year">{t.year}</div>
                    <h4>{t.title}</h4>
                    <p>{t.desc}</p>
                  </div>
                )}
              </div>
              <div className="tl-side" style={{ textAlign: i % 2 === 1 ? 'left' : 'right', paddingLeft: i % 2 === 1 ? 40 : 0, paddingRight: i % 2 === 0 ? 0 : 40 }}>
                {i % 2 === 1 && (
                  <div className="tl-content">
                    <div className="tl-year">{t.year}</div>
                    <h4>{t.title}</h4>
                    <p>{t.desc}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── TESTIMONIALS ─── */}
      <div className="testimonials">
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="reveal" style={{ textAlign: 'center', maxWidth: 520, margin: '0 auto 0' }}>
            <div className="eyebrow">Client Voices</div>
            <h2 className="headline">Trusted by <em>Formulators</em> Worldwide</h2>
          </div>
          <div className="testi-grid">
            {TESTIMONIALS.map((t, i) => (
              <div className={`testi-card reveal stagger-${i + 1}`} key={i}>
                <div className="testi-quote">"</div>
                <div className="testi-stars">★★★★★</div>
                <p>{t.quote}</p>
                <div className="testi-author">
                  <div className="testi-avatar">{t.init}</div>
                  <div>
                    <div className="testi-name">{t.name}</div>
                    <div className="testi-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── CTA BAND ─── */}
      <div className="cta-band">
        <h2>Ready to Partner with Pharmchem?</h2>
        <p>Join 33+ countries that trust our APIs for their pharmaceutical formulations.</p>
        <div className="cta-band-actions">
          <button className="btn btn-white"         onClick={() => navigate('contact')}>Request a Quote Today</button>
          <button className="btn btn-outline-white" onClick={() => navigate('products')}>Browse Product List</button>
        </div>
      </div>
    </>
  );
}

export default Home;
